// Decides whether a song's files must be reloaded into memory (issue #3517 item B1).
//
// The cache refresher rewrites a song's notation, PDF and MIDI in place when the server's copy
// changes, which leaves every URL untouched - only the song's manifest content hash changes. The
// three players all load their file fully into memory, so a cache entry replaced underneath them
// has no effect until they load it again.
//
// Reloading in memory is safe while nothing is playing and disruptive while something is, so the
// download walk always proceeds (see music_cache_refresh.ts) and only the in-memory reload is
// gated here - by the playback update gate (update_gate.ts), not by the raw playing flag: a song
// paused a moment ago counts as playing for the gate's idle window, so a leader pausing between
// verses never sees the PDF or the notation swap under them (issue #3265).
//
// A song that stays playing across a hash change used to keep its old files until the next hash
// change or the next song, because nothing re-evaluated when playback stopped. Issue #3265 PR C
// closes that: a loader that decides 'none' *only* because playback is running
// (isReloadDeferredByPlayback) calls noteSongReloadDeferred(), which registers one update on the
// playback gate (update_gate.ts) under the song-files key. When the gate opens - a song transition
// that lands stopped, or ten minutes after playback stops, never while an auto-playing playlist
// continues - the song reload pulse below advances, every loader re-decides, and with playback now
// stopped each one gets 'same-song-refresh' and reloads in place.
//
// The pulse is deliberately a bare counter rather than a description of what changed: the decision
// is already a pure function of what each loader has versus what the manifest now says, so the
// pulse only has to say "ask again". That is also why a deferral that becomes moot needs no
// machinery to cancel it: if a different song is loaded before the gate opens, that loader has
// already reloaded for 'new-song', and the pulse makes every loader re-decide once more and get
// 'none' (same song, same hash). A pulse with nothing deferred cannot happen, since the gate
// update is only ever registered by a deferral.
//
// React-free on purpose: pure functions plus a tiny store, unit tested directly, shared by the
// notation fetch (current_song.tsx), the PDF load (pdf_viewer.tsx) and the audio player
// (sequencer.tsx). The useSyncExternalStore wrapper around the pulse lives in the React sibling
// use_song_reload_pulse.ts.

import { getPlaybackSnapshot } from './playback_state.ts';
import { canApplyUpdateNow, runWhenUpdateAllowed } from './update_gate.ts';

export interface SongFileVersion {
  // identifies the song for the loader asking the question: the song slug for the sequencer, the
  // file's url for the notation and PDF loaders (one file per song, so either is 1:1 with a song)
  slug: string | undefined;
  // the song's manifest content hash, absent for manifests that do not publish one
  hash: string | undefined;
}

export type SongReloadAction =
  // nothing to do: same song, same content (or no way to tell)
  | 'none'
  // same song, new content, and the playback gate is open: reload the files in place
  | 'same-song-refresh'
  // a different song (or the first one): load it as usual
  | 'new-song';

export function shouldReloadForHashChange({
  previous,
  next,
  isHeld,
}: {
  previous: SongFileVersion | undefined;
  next: SongFileVersion | undefined;
  // whether the playback gate currently holds reloads back (isSongReloadHeld())
  isHeld: boolean;
}): SongReloadAction {
  if (!previous && !next) {
    return 'none';
  }
  if (!previous || !next || previous.slug !== next.slug) {
    return 'new-song';
  }
  // Without a hash on both sides there is no way to know whether the content changed, and
  // reloading on every manifest refresh is exactly what issue #3517 item A3 set out to stop.
  if (!previous.hash || !next.hash || previous.hash === next.hash) {
    return 'none';
  }
  return isHeld ? 'none' : 'same-song-refresh';
}

// What every loader passes as isHeld: the playback gate's view of the moment. Open when nothing is
// playing and either the current song was never played or playback has been idle past the gate's
// window; shut while playing and for the window after a pause or stop.
export function isSongReloadHeld(now: number = Date.now()): boolean {
  return !canApplyUpdateNow(getPlaybackSnapshot(), now);
}

// True when the only thing standing between this song and a reload is that the gate is shut: the
// same song, a different content hash, and a hash known on both sides. The gate opening is then
// the one event that changes the answer, which is exactly what it reports.
export function isReloadDeferredByPlayback(args: {
  previous: SongFileVersion | undefined;
  next: SongFileVersion | undefined;
  isHeld: boolean;
}): boolean {
  return args.isHeld && shouldReloadForHashChange({ ...args, isHeld: false }) === 'same-song-refresh';
}

// The gate key the deferred song reload is held under; distinct from app_update.ts's 'app-reload',
// so holding one back never evicts the other.
const songFilesUpdateKey = 'song-files';

let songReloadPulse = 0;
let isDeferralPending = false;
const pulseListeners = new Set<() => void>();

// Called by a loader that has just deferred a reload because the gate is shut. Idempotent while a
// deferral is pending: all three loaders defer the same hash change, and they share one gate
// update and one pulse. Safe to call during render - a deferral means the gate is shut, so
// `releaseDeferredSongReloads` can never run synchronously from here.
export function noteSongReloadDeferred(): void {
  if (isDeferralPending) {
    return;
  }
  isDeferralPending = true;
  runWhenUpdateAllowed(releaseDeferredSongReloads, { key: songFilesUpdateKey });
}

function releaseDeferredSongReloads(): void {
  isDeferralPending = false;
  songReloadPulse += 1;
  // copy the set so a listener that unsubscribes itself does not disturb the iteration
  for (const listener of [...pulseListeners]) {
    listener();
  }
}

// Advances once each time the gate released deferred song reloads. React-free, for the store and
// for direct unit tests; components read it through useSongReloadPulse().
export function getSongReloadPulse(): number {
  return songReloadPulse;
}

export function subscribeSongReloadPulse(listener: () => void): () => void {
  pulseListeners.add(listener);
  return () => {
    pulseListeners.delete(listener);
  };
}

// test-only: the pulse and the pending deferral are module state
export function resetSongReloadForTests(): void {
  songReloadPulse = 0;
  isDeferralPending = false;
  pulseListeners.clear();
}
